import React, { useState } from 'react';
import { Tip } from '../../types/crime-game';
import { Search, Volume2, VolumeX, Languages } from 'lucide-react';
import { ClickableText } from './ClickableText';

interface CluePlayerProps {
  tips: Tip[];
  revealedCount: number;
  onNarrateTip: (text: string, tipId: string, voice?: string) => void;
  onTranslateTip: (tipId: string, tipText: string) => Promise<string | null>;
  onWordClick?: (word: string) => void;
  isSpeechSupported: boolean;
  playingId: string | null;
  isSpeaking: boolean;
  suspectNames: Map<string, string>; // Map suspectId to name
  translationPenalty: number;
  tipTranslations: Map<string, string>;
}

const tipTypeLabels: Record<string, string> = {
  alibi: 'Alibi',
  contradiction: 'Contradiction',
  motive: 'Motive',
  evidence: 'Evidence',
  detail: 'Detail',
  access: 'Access'
};

const tipTypeColors: Record<string, string> = {
  alibi: 'bg-blue-50 border-blue-200 text-blue-800',
  contradiction: 'bg-red-50 border-red-200 text-red-800',
  motive: 'bg-purple-50 border-purple-200 text-purple-800',
  evidence: 'bg-green-50 border-green-200 text-green-800',
  detail: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  access: 'bg-orange-50 border-orange-200 text-orange-800'
};

export const CluePlayer: React.FC<CluePlayerProps> = ({
  tips,
  revealedCount,
  onNarrateTip,
  onTranslateTip,
  onWordClick,
  isSpeechSupported,
  playingId,
  isSpeaking,
  suspectNames,
  translationPenalty,
  tipTranslations
}) => {
  const revealedTips = tips.slice(0, revealedCount);
  const [translatingTips, setTranslatingTips] = useState<Set<string>>(new Set());

  const handleTranslateTip = async (tip: Tip) => {
    if (tipTranslations.has(tip.id) || translatingTips.has(tip.id)) return;
    setTranslatingTips(prev => new Set(prev).add(tip.id));
    await onTranslateTip(tip.id, tip.text);
    setTranslatingTips(prev => {
      const next = new Set(prev);
      next.delete(tip.id);
      return next;
    });
  };

  const handlePlayTip = (tip: Tip) => {
    onNarrateTip(tip.text, tip.id, tip.voice);
  };

  return (
    <div className="w-full max-w-3xl mx-auto my-6 space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-gray-800 flex items-center">
          <Search className="w-5 h-5 mr-2" />
          Clues Log
        </h3>
        <span className="text-sm text-gray-500">
           {revealedCount} / {tips.length} Revealed
        </span>
      </div>

      <div className="space-y-3 min-h-[200px]">
         {revealedTips.map((tip, idx) => {
           const suspectName = suspectNames.get(tip.suspectId) || 'Unknown';
           const typeColor = tipTypeColors[tip.type] || tipTypeColors.detail;
           const typeLabel = tipTypeLabels[tip.type] || tip.type;
           const isTranslated = tipTranslations.has(tip.id);
           const translatedText = tipTranslations.get(tip.id);
           const isThisTipPlaying = isSpeaking && playingId === tip.id;

           return (
             <div
               key={tip.id}
               className={`flex items-start p-4 border rounded-lg animate-fade-in-up ${typeColor}`}
             >
               <span className="font-mono font-bold mr-3 flex-shrink-0">#{idx + 1}</span>
               <div className="flex-1">
                 <div className="flex items-center gap-2 mb-2">
                   <span className="text-xs font-semibold uppercase tracking-wide opacity-75">
                     {typeLabel}
                   </span>
                   <span className="text-xs opacity-60">•</span>
                   <span className="text-xs font-medium opacity-75">
                     {suspectName}
                   </span>
                 </div>
                 <div className="text-gray-800 leading-relaxed">
                   {isTranslated && translatedText ? (
                     <>
                       <p><ClickableText text={translatedText} onWordClick={onWordClick} /></p>
                       <p className="text-sm text-gray-500 italic mt-1">
                         <ClickableText text={tip.text} onWordClick={onWordClick} />
                       </p>
                     </>
                   ) : (
                     <p><ClickableText text={tip.text} onWordClick={onWordClick} /></p>
                   )}
                 </div>
               </div>
               <div className="ml-3 flex flex-col gap-2 flex-shrink-0">
                 {isSpeechSupported && (
                   <button
                     onClick={() => handlePlayTip(tip)}
                     className={`transition-colors ${
                       isThisTipPlaying ? 'text-indigo-600' : 'text-gray-400 hover:text-indigo-600'
                     }`}
                     title="Play audio"
                   >
                     {isThisTipPlaying ? (
                       <Volume2 className="w-5 h-5 animate-pulse" />
                     ) : (
                       <Volume2 className="w-5 h-5" />
                     )}
                   </button>
                 )}
                 {!isTranslated && (
                   <button
                     onClick={() => handleTranslateTip(tip)}
                     disabled={translatingTips.has(tip.id)}
                     className="text-gray-400 hover:text-indigo-600 transition-colors disabled:opacity-50"
                     title={`Translate to Portuguese (-${translationPenalty} points)`}
                   >
                     <Languages className="w-4 h-4" />
                   </button>
                 )}
               </div>
             </div>
           );
         })}

         {revealedCount === 0 && (
            <div className="text-center text-gray-400 py-8 italic">
              No clues revealed yet. Start investigating!
            </div>
         )}
      </div>
    </div>
  );
};
